import React from 'react';

const ExamplePage = () => {
  const navigationTabs = [
    'Claim', 'Insurance', 'Provider', 'Prescription', 
    'Payee', 'Diagnosis', 'Procedure', 'Supporting', 'Accident'
  ];

  const fieldDefinitions = [
    { name: 'identifier', value: '[ Identifier ] - Business Identifier for claim' },
    { name: 'traceNumber', value: '[ Identifier ] - Number for tracking' },
    { name: 'status', value: '<code> - active | cancelled | draft | entered-in-error' },
    { name: 'type', value: '[ CodeableConcept ] - Category or discipline' },
    { name: 'subType', value: '[ CodeableConcept ] - More granular claim type' },
    { name: 'use', value: '<code> - claim | preauthorization | predetermination' },
    { name: 'patient', value: '[ Reference(Patient) ] - The recipient of the products and services' },
    { name: 'billablePeriod', value: '[ Period ] - Relevant time frame for the claim' },
    { name: 'enterer', value: '[ Reference(Patient|Practitioner|PractitionerRole|RelatedPerson) ] - Author of the claim' },
    { name: 'insurer', value: '[ Reference(Organization) ] - Target' },
    { name: 'provider', value: '[ Reference(Organization|Practitioner|PractitionerRole) ] - Party responsible for the claim' },
    { name: 'priority', value: '[ CodeableConcept ] - Desired processing urgency' }
  ];

  const actionItems = [
    { status: 'red', claim: '394391K', remediation: 'AI Generated' },
    { status: 'red', claim: '394394H', remediation: 'AI Generated' },
    { status: 'red', claim: '493773', remediation: 'AI Generated' },
    { status: 'yellow', claim: '394391K', remediation: 'AI Generated' },
    { status: 'yellow', claim: '394394H', remediation: 'AI Generated' },
    { status: 'yellow', claim: '493773', remediation: 'AI Generated' },
    { status: 'yellow', claim: '494893', remediation: 'AI Generated' }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation Tabs - Centered under header */}
      <div className="w-full bg-gray-900 py-4">
        <div className="container mx-auto flex justify-center">
          <div className="flex space-x-1">
            {navigationTabs.map((tab, index) => (
              <button
                key={index}
                className="px-6 py-2 text-sm font-medium bg-custom-blue text-white first:rounded-l-md last:rounded-r-md hover:bg-opacity-90 transition-colors"
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left Column - Progress Against Tasks */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-custom-blue mb-4">Progress Against Tasks Assigned</h2>
            <div className="flex flex-col space-y-3">
              {[1, 2, 3, 4, 5, 6].map((measure) => (
                <div key={measure} className="bg-gray-700 rounded p-3">
                  <div className="text-sm text-gray-300">Measure {measure}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Middle Column - Field Definitions */}
          <div className="lg:col-span-7 bg-gray-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-custom-blue mb-4">Field Name</h2>
            <div className="space-y-2 text-sm">
              {fieldDefinitions.map((field, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 border-b border-gray-700 py-1">
                  <div className="col-span-4 font-medium text-gray-300">{field.name}</div>
                  <div className="col-span-8 text-gray-400">{field.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Action Items and Details */}
          <div className="lg:col-span-3 space-y-4">
            {/* Action Items */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-custom-blue mb-4">Action Items</h2>
              <div className="space-y-2">
                {actionItems.map((item, index) => (
                  <div key={index} className="flex items-center space-x-4 py-2 border-b border-gray-700">
                    <div className={`w-3 h-3 rounded-full ${item.status === 'red' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                    <div className="text-gray-300">{item.claim}</div>
                    <div className="text-custom-blue">{item.remediation}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Claim Details */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-custom-blue mb-2">Claim Selected - 394391K</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-300 mb-2">Remediation Needed:</h4>
                  <p className="text-gray-400 text-sm">
                    Claim has been denied because the submitted billing information contains a coding error. 
                    The ICD-10 diagnosis code provided does not correspond with the CPT procedure code listed for the service rendered.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-300 mb-2">Recommended Modifications to Claim</h4>
                  <div className="space-y-3">
                    <p className="text-custom-blue">Claim Dental Notification - Incorrect Modifier Application</p>
                    <p className="text-gray-300">Claim Number: 112233455</p>

                    <div>
                      <h5 className="font-medium text-gray-300">Reason for Denial:</h5>
                      <p className="text-gray-400 text-sm">
                        Your claim has been rejected due to an incorrect modifier being applied. 
                        The modifier used does not reflect the circumstances of the service performed and conflicts with the paired CPT code.
                      </p>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-300">Correction Needed:</h5>
                      <p className="text-gray-400 text-sm">
                        Adjust the modifier to one that correctly indicates the service details and is supported by your documentation.
                      </p>
                    </div>

                    <div className="flex justify-between items-center pt-2">
                      <div className="font-bold text-gray-300">PNB94</div>
                      <div className="space-x-2">
                        <button className="px-4 py-2 bg-blue-600 text-white rounded text-sm">TRAIN MODEL</button>
                        <button className="px-4 py-2 bg-green-600 text-white rounded text-sm">APPLY CHANGE</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamplePage; 